import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BsSendFill } from 'react-icons/bs';
import "./ChatFooter.css";
import { StyledComponentsThemWrapper } from "../StyledComponentsThemWrapper";
import { getCTATypeFromUrl, getCTAConfig } from "../../utils/urlPatterns";
import { createTranslator, getBrowserLanguage } from "../../utils/i18n";

// Paper plane icon will be rendered via react-icons BsSendFill

const ChatFooter = ({ 
  onSendMessage, 
  onSendFile,
  onSendAudio,
  isLoading,
  isHumanControlActive,
  inputValue: propInputValue,
  setInputValue: propSetInputValue,
  predictedQuestions = [],
  onQuestionClick,
  isMobile,
  onQuestionsVisibilityChange,
  isCarSearchOpen,
  areQuestionsVisible,
  setAreQuestionsVisible,
  isCompactMode,
  openedByTrigger,
  onCTAClick,
  disabled = false,
  isWaitingForOperator = false,
  isConnected = false,
  isAuthenticated = false,
  statusReceived = false,
  // New props for inline search actions
  inlineSearchActions = null,
  // Reply message props
  replyMessage = null,
  onClearReply = null
}) => {
  // Use local state if props are not provided
  const [localInputValue, setLocalInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [disableAutoFocus, setDisableAutoFocus] = useState(false);
  const [alternatePlaceholder, setAlternatePlaceholder] = useState(false);
  const [ctaPlaceholder, setCtaPlaceholder] = useState('');
  const [currentUrl, setCurrentUrl] = useState(window.location.href);
  const placeholderTimerRef = useRef(null);
  
  // Add state to control when to show human control-dependent elements
  const [showHumanControlElements, setShowHumanControlElements] = useState(false);
  const humanControlTimerRef = useRef(null);
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  
  // Determine if we have valid questions to show
  const hasValidQuestions = Array.isArray(predictedQuestions) && predictedQuestions.length > 0;
  
  const messageInputRef = useRef(null);
  
  // Use either prop state or local state
  const inputValue = propInputValue !== undefined ? propInputValue : localInputValue;
  const setInputValue = propSetInputValue || setLocalInputValue;

  // Auto-resize textarea when value is set programmatically (e.g. CTA populate)
  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.height = 'auto';
      const maxPx = isMobile ? 180 : 220;
      el.style.height = Math.min(el.scrollHeight, maxPx) + 'px';
      el.style.overflowY = el.scrollHeight > maxPx ? 'auto' : 'hidden';
    });
  }, [inputValue, isMobile]);

  // Add a ref to track previous loading state
  const prevLoadingRef = useRef(isLoading);
  const prevHumanControlRef = useRef(isHumanControlActive);
  
  // Add a ref for the send button
  const sendButtonRef = useRef(null);
  const sendLockRef = useRef(false);
  const baseTextareaHeightRef = useRef(null);
  
  const resetTextareaHeight = useCallback(() => {
    const el = messageInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (baseTextareaHeightRef.current) {
        el.style.height = baseTextareaHeightRef.current + 'px';
      } else {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      }
      el.style.overflowY = 'hidden';
    });
  }, []);

  // Capture the initial single-line height when the component mounts
  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const originalValue = el.value;
      // Measure height with a single character to match the first-typed-line height on mobile
      el.value = originalValue || 'X';
      el.style.height = 'auto';
      const base = el.scrollHeight;
      // Restore original value
      el.value = originalValue;
      baseTextareaHeightRef.current = base;
      el.style.height = base + 'px';
      el.style.overflowY = 'hidden';
    });
  }, []);
  
  // Calculate if we are in reduced mobile mode (compact mode or opened by trigger)
  const isReducedMode = isMobile && (isCompactMode || openedByTrigger);

  // Calculate if send button should be visible
  const canSendMessage = inputValue.trim().length > 0 && !isLoading && !disabled;

  // Show human control elements when WebSocket status is received
  useEffect(() => {
    if (isConnected && isAuthenticated && statusReceived) {
      console.log('✅ ChatFooter: WebSocket status received - showing human control elements');
      setShowHumanControlElements(true);
    } else {
      console.log('⏳ ChatFooter: WebSocket status not ready, hiding human control elements', {
        isConnected,
        isAuthenticated,
        statusReceived
      });
      setShowHumanControlElements(false);
    }
  }, [isConnected, isAuthenticated, statusReceived]);

  // Update CTA placeholder when the component mounts or when the URL changes
  useEffect(() => {
    // Get initial CTA placeholder
    const updateCtaPlaceholder = () => {
      const ctaType = getCTATypeFromUrl(window.location.href);
      const { text } = getCTAConfig(ctaType, language);
      setCtaPlaceholder(text);
    };
    
    // Set initial placeholder
    updateCtaPlaceholder();
    
    // Set up listener for URL changes
    const handleLocationChange = () => {
      const newUrl = window.location.href;
      if (newUrl !== currentUrl) {
        setCurrentUrl(newUrl);
        updateCtaPlaceholder();
      }
    };
    
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Set up interval to check for programmatic URL changes
    const urlCheckInterval = setInterval(() => {
      const newUrl = window.location.href;
      if (newUrl !== currentUrl) {
        setCurrentUrl(newUrl);
        updateCtaPlaceholder();
      }
    }, 1000); // Check every second
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(urlCheckInterval);
    };
  }, [currentUrl, language]);

  // Set up alternating placeholder timer for compact mode
  useEffect(() => {
    // Only activate alternating placeholders in reduced mobile mode AND when input is empty
    if (isReducedMode && !inputValue.trim()) {
      // Clear existing timer if any
      if (placeholderTimerRef.current) {
        clearInterval(placeholderTimerRef.current);
      }
      
      // Set up the timer to toggle placeholder every 5 seconds
      placeholderTimerRef.current = setInterval(() => {
        setAlternatePlaceholder(prev => !prev);
      }, 5000);
      
      // Start with the default placeholder
      setAlternatePlaceholder(false);
    } else {
      // If not in reduced mode or user is typing, clear any existing timer
      if (placeholderTimerRef.current) {
        clearInterval(placeholderTimerRef.current);
        placeholderTimerRef.current = null;
      }
      
      // Reset to default placeholder when input is not empty
      if (inputValue.trim()) {
        setAlternatePlaceholder(false);
      }
    }
    
    // Cleanup
    return () => {
      if (placeholderTimerRef.current) {
        clearInterval(placeholderTimerRef.current);
        placeholderTimerRef.current = null;
      }
    };
  }, [isReducedMode, inputValue]);

  // Add effect to reset CTA mode when user starts typing
  useEffect(() => {
    // If user is typing, immediately disable CTA mode
    if (inputValue.trim() && alternatePlaceholder) {
      setAlternatePlaceholder(false);
    }
  }, [inputValue, alternatePlaceholder]);

  // Choose the appropriate placeholder based on mode and alternation state
  const getPlaceholder = () => {
    if (disabled) {
      return t("completeForm");
    }
    
    // Show generic placeholder for first 2 seconds
    if (!showHumanControlElements) {
      return t("writeMessage");
    }
    
    if (isWaitingForOperator) {
      return t("writeRequest");
    }
    
    if (isHumanControlActive) {
      return t("writeMessage");
    }
    
    if (isReducedMode && alternatePlaceholder) {
      return ctaPlaceholder;
    }
    
    return t("writeYourMessage");
  };

  // Function to temporarily disable auto-focus
  const temporarilyDisableAutoFocus = useCallback(() => {
    setDisableAutoFocus(true);
    
    // Re-enable after 10 seconds
    setTimeout(() => {
      setDisableAutoFocus(false);
    }, 10000);
  }, []);

  // Function to scroll to the bottom of the chat
  const scrollToBottom = useCallback(() => {
    if (isMobile) {
      // Find the chat messages container
      const chatMessagesContainer = document.querySelector('.mia-chat-messages');
      if (chatMessagesContainer) {
        // Find the last message or typing indicator
        const lastMessage = chatMessagesContainer.querySelector('.mia-message-container:last-child') || 
                           chatMessagesContainer.querySelector('.mia-loading-message');
        
        // If we found a last message, scroll it into view
        if (lastMessage) {
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback to normal scrolling if no message is found
          chatMessagesContainer.scrollTo({
            top: chatMessagesContainer.scrollHeight,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [isMobile]);

  // Add event listener for clicks on other inputs
  useEffect(() => {
    const handleDocumentClick = (e) => {
      // If the clicked element is an input or textarea but not our message input
      if (
        (e.target.tagName === 'INPUT' || 
         e.target.tagName === 'TEXTAREA' || 
         e.target.getAttribute('contenteditable') === 'true') &&
        e.target !== messageInputRef.current
      ) {
        temporarilyDisableAutoFocus();
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [temporarilyDisableAutoFocus]);

  // Effect to handle loading state changes
  useEffect(() => {
    // If loading state changes from true to false, focus on the input
    if (prevLoadingRef.current && !isLoading) {
      // Focus on input after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    }
    
    // If loading state changes from false to true, focus on the typing indicator
    if (!prevLoadingRef.current && isLoading && isMobile) {
      // Find the typing indicator
      const typingIndicator = document.querySelector('.loading-message') || 
                             document.querySelector('.ai-typing-container');
      
      if (typingIndicator) {
        setTimeout(() => {
          typingIndicator.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
    
    // Update previous loading state
    prevLoadingRef.current = isLoading;
  }, [isLoading, isMobile]);

  // Effect to handle human control state changes
  useEffect(() => {
    // If human control state changes, update the UI
    if (prevHumanControlRef.current !== isHumanControlActive) {
      // Focus on input after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    }
    
    // Update previous human control state
    prevHumanControlRef.current = isHumanControlActive;
  }, [isHumanControlActive]);

  // One-time focus when the chat opens (no recurring interval)
  useEffect(() => {
    if (messageInputRef.current && !disableAutoFocus) {
      messageInputRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle sending a message
  const handleSendMessage = () => {
    if (inputValue.trim() && onSendMessage) {
      // Pass reply info if available
      const replyInfo = replyMessage ? {
        id: replyMessage.id,
        text: replyMessage.text,
        sender: replyMessage.sender
      } : null;
      
      onSendMessage(inputValue.trim(), replyInfo);
      setInputValue('');
      resetTextareaHeight();
      
      // Clear reply after sending
      if (onClearReply) {
        onClearReply();
      }
      
      // Reset typing state
      if (window.chatBodyInstance && window.chatBodyInstance.setTyping) {
        window.chatBodyInstance.setTyping(false);
      }
      
      // Focus on the input after sending a message
      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 100);
    }
  };

  // Prevent double send and trigger immediately on pointer
  const triggerSendNow = useCallback(() => {
    if (sendLockRef.current || !canSendMessage) return;
    sendLockRef.current = true;
    handleSendMessage();
    setTimeout(() => {
      sendLockRef.current = false;
    }, 250);
  }, [canSendMessage, handleSendMessage]);

  // Handle key press events
  const handleKeyDown = (e) => {
    // If Enter is pressed without Shift, send the message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Reset typing state before sending the message
      if (window.chatBodyInstance && window.chatBodyInstance.setTyping) {
        window.chatBodyInstance.setTyping(false);
      }
      
      handleSendMessage();
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    // Set the input value without triggering any scrolling
    setInputValue(e.target.value);
    
    // Prevent any auto-scrolling while typing by temporarily disabling it
    if (window.chatBodyInstance && window.chatBodyInstance.setTyping) {
      window.chatBodyInstance.setTyping(true);
    }
    // Auto-resize textarea to fit content up to a max height
    const el = e.target;
    requestAnimationFrame(() => {
      el.style.height = 'auto';
      const maxPx = isMobile ? 180 : 220;
      el.style.height = Math.min(el.scrollHeight, maxPx) + 'px';
      el.style.overflowY = el.scrollHeight > maxPx ? 'auto' : 'hidden';
    });
  };

  // Add a new function to handle clicks on the chat content
  const handleChatContentClick = useCallback((e) => {
    // Only proceed if we're showing the CTA
    if (isReducedMode && alternatePlaceholder) {
      // Check if the click was NOT on the CTA container or its children
      const ctaContainer = document.querySelector('.mia-message-input-container.mia-cta-active');
      
      // If we clicked outside the CTA container but still in the chat
      if (ctaContainer && !ctaContainer.contains(e.target)) {
        // Reset to normal input mode
        setAlternatePlaceholder(false);
        
        // If we have a message input ref, focus it to allow immediate typing
        if (messageInputRef.current) {
          setTimeout(() => {
            messageInputRef.current.focus();
          }, 100);
        }
      }
    }
  }, [isReducedMode, alternatePlaceholder]);

  // Add an effect to listen for clicks on the chat content
  useEffect(() => {
    // Find the chat container
    const chatContainer = document.querySelector('.mia-ChatAndSearchContainer');
    
    if (chatContainer) {
      chatContainer.addEventListener('click', handleChatContentClick);
      
      // Cleanup
      return () => {
        chatContainer.removeEventListener('click', handleChatContentClick);
      };
    }
  }, [handleChatContentClick]);

  // Modify the main form render to make sure the click handler stops propagation
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Don't send message if in CTA mode
    if (isReducedMode && alternatePlaceholder) {
      // Stop event propagation to prevent triggering the chat content click handler
      e.stopPropagation();
      return false;
    }
    // Intentionally do not send on form submit to avoid host-site form handlers
    return false;
  };

  // Handle input focus
  const handleInputFocus = (e) => {
    setIsInputFocused(true);
  };

  // Handle input blur
  const handleInputBlur = () => {
    setIsInputFocused(false);
    
    // Reset typing state when input loses focus
    if (window.chatBodyInstance && window.chatBodyInstance.setTyping) {
      window.chatBodyInstance.setTyping(false);
    }
  };


  // Handle CTA click
  const handleCTAClick = (e) => {
    // Only trigger if we're showing the CTA placeholder
    if (isReducedMode && alternatePlaceholder && onCTAClick) {
      // Prevent default behavior and stop propagation
      e.preventDefault();
      e.stopPropagation();
      
      // Get current CTA type to pass to the handler
      const ctaType = getCTATypeFromUrl(window.location.href);
      
      // Use setTimeout to break event chain
      setTimeout(() => {
        onCTAClick(ctaType);
      }, 10);
      
      // Prevent any other click handlers from firing
      return false;
    }
  };

  // Handle CTA keyboard events (for accessibility)
  const handleCTAKeyDown = (e) => {
    // Only trigger if we're showing the CTA placeholder
    if (isReducedMode && alternatePlaceholder && onCTAClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get current CTA type to pass to the handler
      const ctaType = getCTATypeFromUrl(window.location.href);
      
      // Use setTimeout to break event chain
      setTimeout(() => {
        onCTAClick(ctaType);
      }, 10);
      
      return false;
    }
  };

  return (
    <StyledComponentsThemWrapper>
      <div className={`mia-chat-footer ${isReducedMode ? 'reduced-mode' : ''} ${inlineSearchActions ? 'has-inline-actions' : ''} transparent-footer`}>
        {/* Inline Search Actions - positioned above input */}
        {inlineSearchActions && (
          <div className="mia-inline-search-actions-bar">
            {inlineSearchActions.canGoBack && (
              <button 
                onClick={inlineSearchActions.onBack} 
                className="mia-inline-action-button mia-back-button"
              >
                ← {t("back")}
              </button>
            )}
            {inlineSearchActions.canGoNext && (
              <button
                onClick={inlineSearchActions.onNext}
                disabled={!inlineSearchActions.canProceed}
                className="mia-inline-action-button mia-next-button"
              >
                {t("next")} →
              </button>
            )}
            {inlineSearchActions.canSubmit && (
              <button
                onClick={inlineSearchActions.onSubmit}
                disabled={!inlineSearchActions.canProceed}
                className="mia-inline-action-button mia-submit-button"
              >
                {inlineSearchActions.submitText || t("send")}
              </button>
            )}
            {inlineSearchActions.onClose && (
              <button 
                onClick={inlineSearchActions.onClose}
                className="mia-inline-action-button mia-close-button"
                aria-label={t("close")}
              >
                ✕
              </button>
            )}
          </div>
        )}
        
        {/* Reply preview - WhatsApp style */}
        {replyMessage && (
          <div className="mia-reply-preview">
            <div className="mia-reply-preview-content">
              <div className="mia-reply-preview-indicator"></div>
              <div className="mia-reply-preview-text">
                <div className="mia-reply-preview-sender">
                  {replyMessage.sender === 'bot' ? 'MIA' : t("me")}
                </div>
                <div className="mia-reply-preview-message">
                  {replyMessage.text.length > 50 
                    ? replyMessage.text.substring(0, 50) + '...' 
                    : replyMessage.text}
                </div>
              </div>
              <button
                className="mia-reply-preview-close"
                onClick={() => onClearReply && onClearReply()}
                aria-label={t("cancelReply")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Simplified input container */}
        <form className="mia-footer-form" onSubmit={handleFormSubmit}>
          <div className="mia-input-wrapper">
            {/* Input field */}
            <div 
              className={`mia-message-input-container ${inputValue ? 'mia-has-content' : ''} ${isLoading ? 'mia-disabled' : ''} ${isReducedMode && alternatePlaceholder ? 'mia-cta-active' : ''} ${isWaitingForOperator ? 'mia-waiting-for-operator' : ''}`}
              onClick={isReducedMode && alternatePlaceholder ? handleCTAClick : undefined}
              onTouchStart={isReducedMode && alternatePlaceholder ? handleCTAClick : undefined}
              onKeyDown={isReducedMode && alternatePlaceholder ? handleCTAKeyDown : undefined}
              role={isReducedMode && alternatePlaceholder ? "button" : undefined}
              aria-label={isReducedMode && alternatePlaceholder ? getPlaceholder() : undefined}
              tabIndex={isReducedMode && alternatePlaceholder ? 0 : undefined}
              data-cta={isReducedMode && alternatePlaceholder ? getCTATypeFromUrl(window.location.href) : null}
            >
              <textarea
                className="mia-message-input"
                placeholder={getPlaceholder()}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={isLoading || (isReducedMode && alternatePlaceholder) || disabled}
                ref={messageInputRef}
                rows={1}
                style={{ 
                  height: 'auto', 
                  minHeight: '24px', 
                  maxHeight: isMobile ? '180px' : '220px',
                  fontSize: '16px' // Ensure font size is at least 16px
                }}
              />
            </div>
            
            {/* Send button */}
            <button
              type="button"
              className="mia-send-button"
              disabled={!canSendMessage}
              aria-label={t("sendMessage")}
              ref={sendButtonRef}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                triggerSendNow();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                triggerSendNow();
              }}
            >
              <div className="mia-send-icon-container">
                <BsSendFill size={isMobile ? 18 : 20} />
              </div>
            </button>
          </div>
        </form>
      </div>
    </StyledComponentsThemWrapper>
  );
};

export default ChatFooter; 
